/**
 * The pass area of the Travel Card (ADR-011). Loads pinned passes for ONE leg
 * from IndexedDB AFTER the card has painted (the fast lane never waits on
 * this), renders the vector barcode at full brightness above the pin-time
 * raster, and offers the one pin action when nothing is pinned yet.
 *
 * Failure honesty: a pin that fails to parse or fails the read-back proof
 * reports pass.pin_failed — never a silent no-op, never a fake "pinned".
 */
import { useEffect, useRef, useState } from "react";
import { t, type Locale } from "../i18n.ts";
import { pinFile } from "./parse.ts";
import { openPassStore, type PassRecord } from "./store.ts";
import { PassBarcode } from "./barcode.tsx";
import "./pass-slot.css";

export interface PassSlotProps {
  locale: Locale;
  gigId: string;
  travelItemId: string;
  personId: string;
}

export function PassSlot({ locale, gigId, travelItemId, personId }: PassSlotProps) {
  const [passes, setPasses] = useState<PassRecord[]>([]);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const reload = async () => {
    try {
      setPasses(await (await openPassStore()).listPassesFor(travelItemId));
    } catch {
      /* store unavailable (private mode) — the card works without passes */
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload is stable per leg
  }, [travelItemId]);

  const onPick = async (file: File) => {
    setBusy(true);
    setFailed(false);
    try {
      const parsed = await pinFile(file); // NEVER throws
      if (!parsed.ok) {
        setFailed(true);
        return;
      }
      const store = await openPassStore();
      await store.pinPass({
        pass_id: crypto.randomUUID(),
        gig_id: gigId,
        travel_item_id: travelItemId,
        person_id: personId,
        pinned_at: Date.now(),
        raster: parsed.raster,
        barcode_text: parsed.barcode?.text ?? "",
        barcode_format: parsed.barcode?.format ?? "",
        source_name: parsed.source_name,
      }); // rejects if the read-back proof fails → honest failure below
      await reload();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const pass = passes[0]; // newest pin wins the card; older ones stay recoverable in IDB

  return (
    <div className="pass-slot">
      {pass !== undefined ? (
        <PassDisplay locale={locale} pass={pass} />
      ) : (
        <>
          <button
            type="button"
            className="pass-pin-action"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            {t(locale, "pass.pin_action")}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/*"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = ""; // same file re-pickable after a failure
              if (f !== undefined) void onPick(f);
            }}
          />
        </>
      )}
      {failed && <p className="pass-failed">{t(locale, "pass.pin_failed")}</p>}
    </div>
  );
}

function PassDisplay({ locale, pass }: { locale: Locale; pass: PassRecord }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const u = URL.createObjectURL(pass.raster);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [pass.raster]);

  return (
    // White ground, full brightness: this block IS the thing the gate scans.
    <div className="pass-display">
      {pass.barcode_text !== "" && <PassBarcode text={pass.barcode_text} format={pass.barcode_format} />}
      {url !== null && <img className="pass-raster" src={url} alt={t(locale, "pass.barcode_alt")} />}
    </div>
  );
}
